// ---------------------------------------------------------------------------
// Deck import UI (MTG via Scryfall) — owned by the import feature.
//
// Integration contract with the rest of the client:
//   • main.ts imports this module for its side effects.
//   • The table context menu dispatches:
//       window.dispatchEvent(new CustomEvent("tt-import-deck",
//         { detail: { x, z, send: (intent) => void } }))
//     where (x, z) is the table drop point and `send` posts intents to the
//     server. This module listens and shows its dialog.
//
// The dialog collects a pasted deck list (and/or a Moxfield/Archidekt URL),
// previews parsed counts locally via shared/mtgDeck, and sends a single
// {t:"importDeck"} intent. Resolution/spawning is server-side; results show
// up as table objects + log lines. Styles are injected as a <style> tag
// (CSS files aren't bundled) and prefixed .mtgimport- to avoid collisions.
//
// Account integration (optional — dialog works fully signed out): when
// window.tt.account (set by account-ui.ts) reports a signed-in user, the
// dialog shows a "My decks" list (load/delete cloud-saved decks) and a
// "Save to account" checkbox that PUTs the imported list to /decks. It
// re-checks on open and on the "tt-account-changed" event.
// ---------------------------------------------------------------------------

import type { ImportGame } from "../shared/dynamicDefs";
import { deckCounts, parseDeckList } from "../shared/mtgDeck";
import { parseLorcanaList, parsePokemonList, tcgDeckCount } from "../shared/tcgDeck";

interface ImportDetail {
  x: number;
  z: number;
  send: (intent: unknown) => void;
}

interface AccountApi {
  me: () => unknown | null;
  api: (path: string, init?: RequestInit) => Promise<Response>;
}

interface SavedDeck {
  id: number | string;
  source_url: string | null;
  name: string;
  data: { list?: string; url?: string; game?: ImportGame; savedAt?: number } | null;
  updated_at: string;
}

/** Per-game dialog copy + local list parsing for the live preview. */
const GAMES: Record<ImportGame, {
  label: string;
  blurb: string;
  placeholder: string;
  showUrl: boolean;
  count: (list: string) => string;
}> = {
  mtg: {
    label: "MTG",
    blurb: `Paste a deck list (MTGA/Moxfield export, "4 Lightning Bolt", sideboard
           sections all work) or drop in a Moxfield / Archidekt link. Card art
           loads from Scryfall.`,
    placeholder: "4 Lightning Bolt\n4 Monastery Swiftspear (BRO) 144\n\nSideboard\n2 Smash to Smithereens",
    showUrl: true,
    count: (list) => {
      const c = deckCounts(parseDeckList(list));
      if (c.main + c.sideboard + c.commander === 0) return "";
      const bits = [`${c.main} main`];
      if (c.commander) bits.push(`${c.commander} commander`);
      if (c.sideboard) bits.push(`${c.sideboard} sideboard`);
      return bits.join(" · ");
    },
  },
  pokemon: {
    label: "Pokémon",
    blurb: `Paste a PTCG Live / Limitless export ("3 Charizard ex OBF 125").
           Card art loads from pokemontcg.io.`,
    placeholder: "Pokémon: 9\n3 Charizard ex OBF 125\n\nTrainer: 15\n4 Ultra Ball SVI 196\n\nEnergy: 3\n10 Basic {R} Energy SVE 2",
    showUrl: false,
    count: (list) => {
      const n = tcgDeckCount(parsePokemonList(list));
      return n > 0 ? `${n} cards` : "";
    },
  },
  lorcana: {
    label: "Lorcana",
    blurb: `Paste a Dreamborn / Lorcana-app export ("4 Elsa - Spirit of Winter").
           Card art loads from lorcast.com.`,
    placeholder: "4 Elsa - Spirit of Winter\n4 Tipo - Growing Son\n2 Be Prepared",
    showUrl: false,
    count: (list) => {
      const n = tcgDeckCount(parseLorcanaList(list));
      return n > 0 ? `${n} cards` : "";
    },
  },
};

const acct = (): AccountApi | undefined => (window as any).tt?.account;

/**
 * Best-effort deck name: MTGA "Name …" header, else the first card line with
 * count/set noise stripped, else the last URL path segment de-slugged.
 */
export function deriveDeckName(list: string, url: string): string {
  const skip = new Set(["deck", "main", "mainboard", "commander", "sideboard", "companion", "about"]);
  for (const raw of list.split("\n")) {
    const line = raw.trim();
    if (!line || skip.has(line.toLowerCase())) continue;
    if (/^name\s+/i.test(line)) return line.replace(/^name\s+/i, "").slice(0, 60);
    const cleaned = line
      .replace(/^\d+x?\s+/i, "")
      .replace(/\s+\([A-Z0-9]{2,6}\)\s+[\w-]+$/, "")
      .slice(0, 60);
    if (cleaned) return cleaned;
  }
  if (url) {
    try {
      const segs = new URL(url).pathname.split("/").filter(Boolean);
      const last = decodeURIComponent(segs[segs.length - 1] ?? "").replace(/[-_]+/g, " ").trim();
      if (last && !/^\d+$/.test(last)) return last.slice(0, 60);
    } catch { /* not a parseable URL */ }
  }
  return "Imported deck";
}

const CSS = `
.mtgimport-overlay {
  position: absolute;
  inset: 0;
  z-index: 55;
  display: grid;
  place-items: center;
  background: rgba(10, 11, 14, 0.6);
  backdrop-filter: blur(3px);
}
.mtgimport-card {
  width: min(460px, calc(100vw - 40px));
  padding: 22px 24px;
  background: rgba(24, 25, 30, 0.97);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 14px;
  box-shadow: 0 16px 50px rgba(0, 0, 0, 0.6);
  color: #e8e6df;
  font-size: 13.5px;
}
.mtgimport-card h2 {
  font-size: 15px;
  color: #d8b45a;
  margin-bottom: 4px;
}
.mtgimport-tabs {
  display: flex;
  gap: 6px;
  margin-bottom: 10px;
}
.mtgimport-tabs button {
  padding: 5px 14px;
  font-size: 12.5px;
  font-weight: 600;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: rgba(255, 255, 255, 0.06);
  color: #a8a49a;
  cursor: pointer;
}
.mtgimport-tabs button:hover { background: rgba(255, 255, 255, 0.14); color: #e8e6df; }
.mtgimport-tabs button.on {
  background: #d8b45a;
  border-color: #d8b45a;
  color: #221d10;
}
.mtgimport-card p {
  font-size: 12.5px;
  color: #a8a49a;
  margin-bottom: 14px;
}
.mtgimport-card input,
.mtgimport-card textarea {
  width: 100%;
  padding: 9px 12px;
  font-size: 13px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(0, 0, 0, 0.35);
  color: #e8e6df;
  outline: none;
  margin-bottom: 10px;
  font-family: inherit;
}
.mtgimport-card textarea {
  height: 180px;
  resize: vertical;
  font-family: ui-monospace, monospace;
  font-size: 12.5px;
  line-height: 1.5;
}
.mtgimport-card input:focus,
.mtgimport-card textarea:focus { border-color: #d8b45a; }
.mtgimport-status {
  min-height: 18px;
  font-size: 12px;
  color: #9b988e;
  margin-bottom: 10px;
}
.mtgimport-status.error { color: #e5735d; }
.mtgimport-status.busy { color: #d8b45a; }
.mtgimport-row {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}
.mtgimport-row button {
  padding: 8px 18px;
  font-size: 13.5px;
  font-weight: 600;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: rgba(255, 255, 255, 0.08);
  color: #e8e6df;
  cursor: pointer;
  transition: background .15s, filter .15s;
}
.mtgimport-row button:hover { background: rgba(255, 255, 255, 0.16); }
.mtgimport-row button.mtgimport-go {
  background: #d8b45a;
  border-color: #d8b45a;
  color: #221d10;
}
.mtgimport-row button.mtgimport-go:hover { filter: brightness(1.08); }
.mtgimport-row button:disabled { opacity: 0.55; cursor: default; }
.mtgimport-account {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 10px;
  padding-top: 12px;
}
.mtgimport-decks-title {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #9b988e;
  margin-bottom: 6px;
}
.mtgimport-decks-list { max-height: 132px; overflow-y: auto; margin-bottom: 10px; }
.mtgimport-decks-empty { color: #77746c; font-size: 12px; padding: 2px 6px 6px; }
.mtgimport-deck {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 6px;
  border-radius: 6px;
  font-size: 12.5px;
}
.mtgimport-deck:hover { background: rgba(255, 255, 255, 0.06); }
.mtgimport-deck-name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mtgimport-deck-date { color: #77746c; font-size: 11px; white-space: nowrap; }
.mtgimport-deck button {
  padding: 2px 9px;
  font-size: 11.5px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: rgba(255, 255, 255, 0.08);
  color: #e8e6df;
  cursor: pointer;
}
.mtgimport-deck button:hover { background: rgba(255, 255, 255, 0.16); }
.mtgimport-deck button.mtgimport-del:hover { background: rgba(229, 115, 93, 0.3); }
.mtgimport-saverow { display: flex; align-items: center; gap: 10px; }
.mtgimport-card .mtgimport-saverow input.mtgimport-name { flex: 1; width: auto; margin-bottom: 0; }
.mtgimport-saverow label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: #a8a49a;
  white-space: nowrap;
  cursor: pointer;
}
.mtgimport-card .mtgimport-saverow input[type="checkbox"] {
  width: auto;
  margin: 0;
  accent-color: #d8b45a;
}
`;

class ImportDialog {
  private overlay: HTMLDivElement;
  private urlInput: HTMLInputElement;
  private listInput: HTMLTextAreaElement;
  private status: HTMLDivElement;
  private importBtn: HTMLButtonElement;
  private accountEl: HTMLDivElement;
  private decksList: HTMLDivElement;
  private nameInput: HTMLInputElement;
  private saveCheck: HTMLInputElement;
  /** True once the user typed a deck name; stops auto-derivation. */
  private nameEdited = false;
  private detail: ImportDetail | null = null;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;
  private game: ImportGame = "mtg";
  private tabsEl: HTMLDivElement;
  private blurbEl: HTMLParagraphElement;

  constructor() {
    const style = document.createElement("style");
    style.textContent = CSS;
    document.head.appendChild(style);

    this.overlay = document.createElement("div");
    this.overlay.className = "mtgimport-overlay";
    this.overlay.style.display = "none";
    this.overlay.innerHTML = `
      <div class="mtgimport-card">
        <h2>Import Deck</h2>
        <div class="mtgimport-tabs"></div>
        <p class="mtgimport-blurb"></p>
        <input class="mtgimport-url" type="text" spellcheck="false"
               placeholder="Deck URL — moxfield.com / archidekt.com (optional)">
        <textarea class="mtgimport-list" spellcheck="false"></textarea>
        <div class="mtgimport-account" style="display:none">
          <div class="mtgimport-decks-title">My decks</div>
          <div class="mtgimport-decks-list"></div>
          <div class="mtgimport-saverow">
            <input class="mtgimport-name" type="text" spellcheck="false"
                   placeholder="Deck name">
            <label><input class="mtgimport-save" type="checkbox" checked>
              Save to account</label>
          </div>
        </div>
        <div class="mtgimport-status"></div>
        <div class="mtgimport-row">
          <button class="mtgimport-cancel" type="button">Cancel</button>
          <button class="mtgimport-go" type="button">Import</button>
        </div>
      </div>`;
    document.body.appendChild(this.overlay);

    this.tabsEl = this.overlay.querySelector<HTMLDivElement>(".mtgimport-tabs")!;
    this.blurbEl = this.overlay.querySelector<HTMLParagraphElement>(".mtgimport-blurb")!;
    for (const [id, cfg] of Object.entries(GAMES) as Array<[ImportGame, (typeof GAMES)["mtg"]]>) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.dataset.game = id;
      btn.textContent = cfg.label;
      btn.onclick = () => this.setGame(id);
      this.tabsEl.appendChild(btn);
    }
    this.urlInput = this.overlay.querySelector<HTMLInputElement>(".mtgimport-url")!;
    this.listInput = this.overlay.querySelector<HTMLTextAreaElement>(".mtgimport-list")!;
    this.status = this.overlay.querySelector<HTMLDivElement>(".mtgimport-status")!;
    this.importBtn = this.overlay.querySelector<HTMLButtonElement>(".mtgimport-go")!;
    this.accountEl = this.overlay.querySelector<HTMLDivElement>(".mtgimport-account")!;
    this.decksList = this.overlay.querySelector<HTMLDivElement>(".mtgimport-decks-list")!;
    this.nameInput = this.overlay.querySelector<HTMLInputElement>(".mtgimport-name")!;
    this.saveCheck = this.overlay.querySelector<HTMLInputElement>(".mtgimport-save")!;
    this.nameInput.addEventListener("input", () => {
      this.nameEdited = this.nameInput.value.trim() !== "";
    });
    window.addEventListener("tt-account-changed", () => {
      if (this.overlay.style.display !== "none") this.refreshAccount();
    });

    this.overlay.querySelector(".mtgimport-cancel")!.addEventListener("click", () => this.close());
    this.overlay.addEventListener("pointerdown", (e) => {
      if (e.target === this.overlay) this.close();
    });
    window.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.overlay.style.display !== "none") this.close();
    });
    this.listInput.addEventListener("input", () => this.preview());
    this.urlInput.addEventListener("input", () => this.preview());
    this.importBtn.addEventListener("click", () => this.submit());
  }

  open(detail: ImportDetail): void {
    this.detail = detail;
    if (this.closeTimer) { clearTimeout(this.closeTimer); this.closeTimer = null; }
    this.overlay.style.display = "grid";
    this.importBtn.disabled = false;
    this.nameEdited = false;
    this.setGame(this.game);
    this.refreshAccount();
    this.listInput.focus();
  }

  /** Switch importer tab: copy, URL row visibility, preview parser. */
  private setGame(game: ImportGame): void {
    this.game = game;
    const cfg = GAMES[game];
    for (const b of this.tabsEl.querySelectorAll("button")) {
      b.classList.toggle("on", b.dataset.game === game);
    }
    this.blurbEl.textContent = cfg.blurb.replace(/\s+/g, " ");
    this.listInput.placeholder = cfg.placeholder;
    this.urlInput.style.display = cfg.showUrl ? "" : "none";
    this.preview();
  }

  close(): void {
    this.overlay.style.display = "none";
    this.detail = null;
    if (this.closeTimer) { clearTimeout(this.closeTimer); this.closeTimer = null; }
  }

  /** Live parse preview so typos are visible before hitting the server. */
  private preview(): void {
    this.status.className = "mtgimport-status";
    const cfg = GAMES[this.game];
    const url = cfg.showUrl ? this.urlInput.value.trim() : "";
    const counted = cfg.count(this.listInput.value);
    this.status.textContent =
      counted || (url ? "Deck will be fetched from the URL." : "");
    if (!this.nameEdited && this.accountEl.style.display !== "none") {
      this.nameInput.value =
        counted || url ? deriveDeckName(this.listInput.value, url) : "";
    }
  }

  // ------------------------------------------------------- account decks

  /** Show/hide the account section based on sign-in state. */
  private refreshAccount(): void {
    const signedIn = !!acct()?.me();
    this.accountEl.style.display = signedIn ? "" : "none";
    if (signedIn) void this.loadDecks();
  }

  private async loadDecks(): Promise<void> {
    const a = acct();
    if (!a?.me()) return;
    this.decksList.innerHTML = `<div class="mtgimport-decks-empty">Loading…</div>`;
    try {
      const res = await a.api("/decks");
      if (!res.ok) throw new Error(String(res.status));
      const { decks } = (await res.json()) as { decks: SavedDeck[] };
      this.renderDecks(decks ?? []);
    } catch {
      this.decksList.innerHTML =
        `<div class="mtgimport-decks-empty">Couldn't load saved decks.</div>`;
    }
  }

  private renderDecks(decks: SavedDeck[]): void {
    this.decksList.innerHTML = "";
    if (decks.length === 0) {
      this.decksList.innerHTML =
        `<div class="mtgimport-decks-empty">No saved decks yet — import one with “Save to account” checked.</div>`;
      return;
    }
    for (const deck of decks) {
      const row = document.createElement("div");
      row.className = "mtgimport-deck";
      const name = document.createElement("span");
      name.className = "mtgimport-deck-name";
      name.textContent = deck.name || "Deck";
      name.title = deck.source_url ?? deck.name;
      const date = document.createElement("span");
      date.className = "mtgimport-deck-date";
      const d = new Date(deck.updated_at);
      date.textContent = Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
      const load = document.createElement("button");
      load.type = "button";
      load.textContent = "Load";
      load.onclick = () => {
        this.setGame(deck.data?.game ?? "mtg");
        this.listInput.value = deck.data?.list ?? "";
        this.urlInput.value = deck.data?.url ?? deck.source_url ?? "";
        this.nameInput.value = deck.name || "";
        this.nameEdited = true;
        this.preview();
      };
      const del = document.createElement("button");
      del.type = "button";
      del.className = "mtgimport-del";
      del.textContent = "✕";
      del.title = "Delete saved deck";
      del.onclick = async () => {
        del.disabled = true;
        try {
          await acct()?.api(`/decks/${deck.id}`, { method: "DELETE" });
        } catch { /* refresh shows the truth either way */ }
        void this.loadDecks();
      };
      row.append(name, date, load, del);
      this.decksList.appendChild(row);
    }
  }

  private submit(): void {
    if (!this.detail) return;
    const cfg = GAMES[this.game];
    const url = cfg.showUrl ? this.urlInput.value.trim() : "";
    const list = this.listInput.value;
    if (!url && !cfg.count(list)) {
      this.status.className = "mtgimport-status error";
      this.status.textContent = cfg.showUrl
        ? "Paste a deck list or enter a deck URL first."
        : "Paste a deck list first.";
      return;
    }
    this.detail.send({
      t: "importDeck",
      list,
      url: url || undefined,
      game: this.game,
      x: this.detail.x,
      z: this.detail.z,
    });
    const a = acct();
    if (a?.me() && this.saveCheck.checked) {
      const name = this.nameInput.value.trim() || deriveDeckName(list, url);
      void a
        .api("/decks", {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            source_url: url || null,
            name,
            data: { list, url: url || undefined, game: this.game, savedAt: Date.now() },
          }),
        })
        .catch(() => { /* import itself already succeeded */ });
    }
    const source = this.game === "pokemon" ? "pokemontcg.io" : this.game === "lorcana" ? "lorcast" : "Scryfall";
    this.status.className = "mtgimport-status busy";
    this.status.textContent = `Resolving cards via ${source} — the deck will appear on the table…`;
    this.importBtn.disabled = true;
    this.closeTimer = setTimeout(() => this.close(), 1600);
  }
}

let dialog: ImportDialog | null = null;

// Guarded so pure helpers (deriveDeckName) are importable from bun test.
if (typeof window !== "undefined") {
  window.addEventListener("tt-import-deck", ((e: CustomEvent<ImportDetail>) => {
    dialog ??= new ImportDialog();
    dialog.open(e.detail);
  }) as EventListener);
}

export {};
